import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Link,
  MenuItem,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { PhotoCameraRounded, SearchRounded } from "@mui/icons-material";
import { Navigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import logoLogin123Zap from "../../../shared/assets/brand/logo-login-123zap.png";
import { authRoutes } from "../config/authRoutes";
import { useCompleteSignupForm } from "../hooks/useCompleteSignupForm";
import {
  cepMask,
  cnpjMask,
  cpfMask,
  flexiblePhoneMask,
  TextMaskCustom,
} from "../../../shared/utils/inputMasks";

export default function CompleteSignupPage() {
  const { logout, user } = useAuth();
  const isEmailVerified = String(user?.emailVerified).toLowerCase() === "true";
  const isProfileComplete = String(user?.completeProfile).toLowerCase() === "true";

  const form = useCompleteSignupForm();

  async function handleLogout() {
    await logout();
  }

  if (!isEmailVerified) {
    return <Navigate to={authRoutes.verifyEmail} replace />;
  }

  if (isProfileComplete) {
    return <Navigate to={authRoutes.dashboard} replace />;
  }

  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.palette.background.default,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        minHeight: "100vh",
        overflowX: "hidden",
        position: "relative",
        px: { xs: 2, sm: 3, md: 5 },
        py: { xs: 3, sm: 4, md: 5 },
        width: "100%",
        "@media (min-height: 840px) and (min-width: 900px)": {
          justifyContent: "center",
        },
        "@media (max-height: 820px) and (min-width: 900px)": {
          py: 3,
        },
        "@media (max-height: 780px) and (min-width: 900px)": {
          py: 2,
        },
      })}
    >
      <LogoutLink onClick={handleLogout} />

      <Box
        sx={{
          mx: "auto",
          width: "100%",
          maxWidth: 1120,
        }}
      >
        <Box
          component="form"
          onSubmit={form.handleSubmit}
          noValidate
          sx={(theme) => ({
            bgcolor: theme.palette.surface.default,
            border: `1px solid ${theme.palette.border.default}`,
            borderRadius: theme.shape.radii.md,
            boxSizing: "border-box",
            mt: { xs: 2, sm: 3, md: 3 },
            overflow: "hidden",
            px: { xs: 2, sm: 3.5, md: 4.5 },
            py: { xs: 3, sm: 4, md: 4 },
            width: "100%",
            "@media (min-height: 840px) and (min-width: 900px)": {
              mt: 0,
            },
            "@media (max-height: 820px) and (min-width: 900px)": {
              mt: 2,
              px: 4,
              py: 3,
            },
            "@media (max-height: 780px) and (min-width: 900px)": {
              px: 3.5,
              py: 2.5,
            },
          })}
        >
          <Box
            key={form.initialLoading ? "complete-signup-loading" : "complete-signup-ready"}
            sx={{
              animation: "completeSignupContentIn 180ms ease-out both",
              "@keyframes completeSignupContentIn": {
                from: {
                  opacity: 0.48,
                  transform: "translateY(4px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            {form.initialLoading ? (
              <CompleteSignupSkeleton />
            ) : (
              <>
              <Box
                sx={(theme) => ({
                  borderBottom: `1px solid ${theme.palette.border.muted}`,
                  mb: { xs: 3, md: 3 },
                  pb: { xs: 3, md: 3 },
                  "@media (max-height: 900px) and (min-width: 900px)": {
                    mb: 2,
                    pb: 2,
                  },
                })}
              >
                <CompleteSignupHeader />
                {form.formMessage ? (
                  <Typography
                    role="alert"
                    sx={(theme) => ({
                      color: theme.palette.error.main,
                      fontSize: theme.typography.caption.fontSize,
                      lineHeight: "18px",
                      mt: 1.5,
                    })}
                  >
                    {form.formMessage}
                  </Typography>
                ) : null}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: { xs: 3, md: 3.5 },
                  gridTemplateColumns: { xs: "1fr", md: "124px minmax(0, 1fr)" },
                  "@media (max-height: 900px) and (min-width: 900px)": {
                    gap: 3,
                  },
                }}
              >
                <ProfilePhotoControl
                  accountInitial={form.accountInitial}
                  helperText={form.imageHelperText}
                  imageLoading={form.imageLoading}
                  onChange={form.handleProfileImageChange}
                  profileImage={form.profileImage}
                />

                <Box sx={{ minWidth: 0 }}>
                  <CompleteSignupSection title="Conta">
                    <Box
                      sx={{
                        display: "grid",
                        gap: { xs: 2, md: 2.25 },
                        gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" },
                        "@media (max-height: 900px) and (min-width: 900px)": {
                          gap: 1.5,
                        },
                      }}
                    >
                      <CompleteSignupField
                        select
                        label="Tipo"
                        name="person_type"
                        value={form.formData.person_type}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.person_type)}
                        helperText={form.errors.person_type}
                      >
                        <MenuItem value="PJ">Pessoa Jurídica (PJ)</MenuItem>
                        <MenuItem value="fisica">Pessoa Física (PF)</MenuItem>
                      </CompleteSignupField>

                      <CompleteSignupField
                        label="Nome da conta / Empresa"
                        name="firstName"
                        value={form.formData.firstName}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.firstName)}
                        helperText={form.errors.firstName}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gap: { xs: 2, md: 2.25 },
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        "@media (max-height: 900px) and (min-width: 900px)": {
                          gap: 1.5,
                        },
                      }}
                    >
                      <CompleteSignupField
                        label={form.isPJ ? "CNPJ" : "CPF"}
                        name="cpf_cnpj"
                        value={form.formData.cpf_cnpj}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.cpf_cnpj)}
                        helperText={form.errors.cpf_cnpj}
                        InputProps={{
                          inputComponent: TextMaskCustom,
                          inputProps: { mask: form.isPJ ? cnpjMask : cpfMask },
                        }}
                      />

                      <CompleteSignupField
                        label="Nome do responsável"
                        name="lastName"
                        value={form.formData.lastName}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.lastName)}
                        helperText={form.errors.lastName}
                      />
                    </Box>
                  </CompleteSignupSection>

                  <CompleteSignupSection title="Administrador">
                    <Box
                      sx={{
                        display: "grid",
                        gap: { xs: 2, md: 2.25 },
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, minmax(0, 1fr))" },
                        "@media (max-height: 900px) and (min-width: 900px)": {
                          gap: 1.5,
                        },
                      }}
                    >
                      <CompleteSignupField
                        label="E-mail"
                        name="email"
                        type="email"
                        value={form.formData.email}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.email)}
                        helperText={form.errors.email}
                      />

                      <CompleteSignupField
                        label="Telefone comercial"
                        name="telephone"
                        value={form.formData.telephone}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.telephone)}
                        helperText={form.errors.telephone}
                        InputProps={{
                          inputComponent: TextMaskCustom,
                          inputProps: { mask: flexiblePhoneMask },
                        }}
                      />

                      <CompleteSignupField
                        label="Celular / WhatsApp"
                        name="cellphone"
                        value={form.formData.cellphone}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.cellphone)}
                        helperText={form.errors.cellphone}
                        InputProps={{
                          inputComponent: TextMaskCustom,
                          inputProps: { mask: flexiblePhoneMask },
                        }}
                      />
                    </Box>
                  </CompleteSignupSection>

                  <CompleteSignupSection title="Endereço">
                    <Box
                      sx={{
                        display: "grid",
                        gap: { xs: 2, md: 2.25 },
                        gridTemplateAreas: {
                          xs: '"zipcode neighborhood" "street street" "number state" "city city"',
                          sm: '"zipcode street number" "neighborhood city state"',
                        },
                        gridTemplateColumns: {
                          xs: "1fr 1fr",
                          sm: "190px minmax(0, 1fr) 140px",
                        },
                        "@media (max-height: 900px) and (min-width: 900px)": {
                          gap: 1.5,
                        },
                      }}
                    >
                      <CompleteSignupField
                        label="CEP"
                        name="zipcode"
                        value={form.formData.zipcode}
                        onBlur={form.handleCepBlur}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.zipcode)}
                        helperText={form.errors.zipcode}
                        sx={{ gridArea: "zipcode" }}
                        InputProps={{
                          inputComponent: TextMaskCustom,
                          inputProps: { mask: cepMask },
                          endAdornment: (
                            <InputAdornment position="end">
                              <SearchRounded fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <CompleteSignupField
                        label="Rua"
                        name="street"
                        value={form.formData.street}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.street)}
                        helperText={form.errors.street}
                        sx={{ gridArea: "street" }}
                      />

                      <CompleteSignupField
                        label="Nº"
                        name="number"
                        value={form.formData.number}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.number)}
                        helperText={form.errors.number}
                        sx={{ gridArea: "number" }}
                      />

                      <CompleteSignupField
                        label="Bairro"
                        name="neighborhood"
                        value={form.formData.neighborhood}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.neighborhood)}
                        helperText={form.errors.neighborhood}
                        sx={{ gridArea: "neighborhood" }}
                      />

                      <CompleteSignupField
                        label="Cidade"
                        name="city"
                        value={form.formData.city}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.city)}
                        helperText={form.errors.city}
                        sx={{ gridArea: "city" }}
                      />

                      <CompleteSignupField
                        label="UF"
                        name="state"
                        value={form.formData.state}
                        onChange={form.handleChange}
                        error={Boolean(form.errors.state)}
                        helperText={form.errors.state}
                        inputProps={{ maxLength: 2 }}
                        sx={{ gridArea: "state" }}
                      />
                    </Box>
                  </CompleteSignupSection>

                </Box>
              </Box>

              <Box
                sx={(theme) => ({
                  borderTop: `1px solid ${theme.palette.border.muted}`,
                  display: "flex",
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  gap: 1.5,
                  justifyContent: "flex-end",
                  mt: { xs: 3, md: 3 },
                  pt: { xs: 2.5, md: 2.5 },
                  "@media (max-height: 900px) and (min-width: 900px)": {
                    mt: 2,
                    pt: 2,
                  },
                })}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={form.loading}
                  sx={{ minWidth: { xs: "100%", sm: 172 } }}
                >
                  {form.loading ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : (
                    "Finalizar cadastro"
                  )}
                </Button>
              </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function LogoutLink({ onClick }) {
  return (
    <Link
      component="button"
      type="button"
      onClick={onClick}
      underline="hover"
      sx={(theme) => ({
        color: theme.palette.text.secondary,
        bgcolor: "transparent",
        border: 0,
        cursor: "pointer",
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        lineHeight: "20px",
        mb: { xs: 0.75, sm: 1 },
        ml: "auto",
        minHeight: 32,
        px: 1.25,
        py: 0.5,
        borderRadius: theme.shape.radii.sm,
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        "&:hover": {
          bgcolor: theme.palette.action.hover,
          color: theme.palette.primary.main,
        },
      })}
    >
      Sair
    </Link>
  );
}

function CompleteSignupSkeleton() {
  return (
    <>
      <Box
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.border.muted}`,
          mb: { xs: 3, md: 3 },
          pb: { xs: 3, md: 3 },
          "@media (max-height: 900px) and (min-width: 900px)": {
            mb: 2,
            pb: 2,
          },
        })}
      >
        <Box
          sx={{
            alignItems: "flex-start",
            display: "flex",
            gap: { xs: 2, sm: 3 },
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="min(280px, 78%)" height={38} />
            <Skeleton variant="text" width="min(340px, 92%)" height={24} sx={{ mt: 0.75 }} />
          </Box>
          <Skeleton
            variant="rounded"
            width={72}
            height={44}
            sx={{ display: { xs: "none", sm: "block" }, flexShrink: 0 }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 3, md: 3.5 },
          gridTemplateColumns: { xs: "1fr", md: "124px minmax(0, 1fr)" },
          "@media (max-height: 900px) and (min-width: 900px)": {
            gap: 3,
          },
        }}
      >
        <Box sx={{ alignItems: "center", display: "flex", flexDirection: "column" }}>
          <Skeleton variant="circular" width={104} height={104} />
          <Skeleton variant="text" width={104} height={20} sx={{ mt: 1.25 }} />
          <Skeleton variant="text" width={72} height={18} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <CompleteSignupSectionSkeleton titleWidth={58}>
            <Box
              sx={{
                display: "grid",
                gap: { xs: 2, md: 2.25 },
                gridTemplateColumns: { xs: "1fr", md: "230px minmax(0, 1fr)" },
                "@media (max-height: 900px) and (min-width: 900px)": {
                  gap: 1.5,
                },
              }}
            >
              <CompleteSignupFieldSkeleton labelWidth={44} />
              <CompleteSignupFieldSkeleton labelWidth={160} />
            </Box>
            <Box
              sx={{
                display: "grid",
                gap: { xs: 2, md: 2.25 },
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                "@media (max-height: 900px) and (min-width: 900px)": {
                  gap: 1.5,
                },
              }}
            >
              <CompleteSignupFieldSkeleton labelWidth={48} />
              <CompleteSignupFieldSkeleton labelWidth={142} />
            </Box>
          </CompleteSignupSectionSkeleton>

          <CompleteSignupSectionSkeleton titleWidth={128}>
            <Box
              sx={{
                display: "grid",
                gap: { xs: 2, md: 2.25 },
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, minmax(0, 1fr))" },
                "@media (max-height: 900px) and (min-width: 900px)": {
                  gap: 1.5,
                },
              }}
            >
              <CompleteSignupFieldSkeleton labelWidth={58} />
              <CompleteSignupFieldSkeleton labelWidth={132} />
              <CompleteSignupFieldSkeleton labelWidth={124} />
            </Box>
          </CompleteSignupSectionSkeleton>

          <CompleteSignupSectionSkeleton titleWidth={90}>
            <Box
              sx={{
                display: "grid",
                gap: { xs: 2, md: 2.25 },
                gridTemplateAreas: {
                  xs: '"zipcode neighborhood" "street street" "number state" "city city"',
                  sm: '"zipcode street number" "neighborhood city state"',
                },
                gridTemplateColumns: {
                  xs: "1fr 1fr",
                  sm: "190px minmax(0, 1fr) 140px",
                },
                "@media (max-height: 900px) and (min-width: 900px)": {
                  gap: 1.5,
                },
              }}
            >
              <CompleteSignupFieldSkeleton labelWidth={42} sx={{ gridArea: "zipcode" }} />
              <CompleteSignupFieldSkeleton labelWidth={36} sx={{ gridArea: "street" }} />
              <CompleteSignupFieldSkeleton labelWidth={32} sx={{ gridArea: "number" }} />
              <CompleteSignupFieldSkeleton labelWidth={54} sx={{ gridArea: "neighborhood" }} />
              <CompleteSignupFieldSkeleton labelWidth={58} sx={{ gridArea: "city" }} />
              <CompleteSignupFieldSkeleton labelWidth={34} sx={{ gridArea: "state" }} />
            </Box>
          </CompleteSignupSectionSkeleton>
        </Box>
      </Box>

      <Box
        sx={(theme) => ({
          borderTop: `1px solid ${theme.palette.border.muted}`,
          display: "flex",
          justifyContent: "flex-end",
          mt: { xs: 3, md: 3 },
          pt: { xs: 2.5, md: 2.5 },
          "@media (max-height: 900px) and (min-width: 900px)": {
            mt: 2,
            pt: 2,
          },
        })}
      >
        <Skeleton variant="rounded" width={172} height={48} sx={{ width: { xs: "100%", sm: 172 } }} />
      </Box>
    </>
  );
}

function CompleteSignupSectionSkeleton({ children, titleWidth }) {
  return (
    <Box
      sx={{
        mt: 0,
        "& + &": { mt: { xs: 3, md: 3 } },
        "@media (max-height: 900px) and (min-width: 900px)": {
          "& + &": { mt: 2 },
        },
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1.5 }}>
        <Skeleton variant="rounded" width={4} height={18} />
        <Skeleton variant="text" width={titleWidth} height={24} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, md: 2 },
          "@media (max-height: 900px) and (min-width: 900px)": {
            gap: 1.5,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function CompleteSignupFieldSkeleton({ labelWidth = 96, sx }) {
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Skeleton variant="text" width={labelWidth} height={20} sx={{ mb: 0.75 }} />
      <Skeleton variant="rounded" height={48} width="100%" />
    </Box>
  );
}

function CompleteSignupHeader() {
  return (
    <Box
      sx={{
        alignItems: "flex-start",
        display: "flex",
        flexDirection: "row",
        gap: { xs: 2, sm: 3 },
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 800,
            lineHeight: 1.22,
            "@media (max-height: 900px) and (min-width: 900px)": {
              fontSize: 25,
            },
          })}
        >
          Complete seu cadastro
        </Typography>
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: theme.typography.body1.fontSize,
            lineHeight: 1.5,
            maxWidth: 640,
            mt: 1.5,
            "@media (max-height: 900px) and (min-width: 900px)": {
              mt: 0.75,
            },
          })}
        >
          Finalize os dados da conta antes do primeiro acesso.
        </Typography>
      </Box>

      <Box
        component="img"
        src={logoLogin123Zap}
        alt="123zap"
        sx={{
          display: { xs: "none", sm: "block" },
          flexShrink: 0,
          height: "auto",
          width: { sm: 68, md: 72 },
          "@media (max-height: 900px) and (min-width: 900px)": {
            width: 62,
          },
          "@media (max-height: 780px) and (min-width: 900px)": {
            width: 56,
          },
        }}
      />
    </Box>
  );
}

function ProfilePhotoControl({
  accountInitial,
  helperText,
  imageLoading,
  onChange,
  profileImage,
}) {
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
      }}
    >
      <Box
        component="label"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.surface.muted,
          backgroundImage: profileImage ? `url(${profileImage})` : "none",
          backgroundPosition: "center",
          backgroundSize: "cover",
          border: `1px solid ${theme.palette.border.default}`,
          borderRadius: theme.shape.radii.pill,
          boxShadow: theme.shadows[2],
          boxSizing: "border-box",
          color: theme.palette.text.disabled,
          cursor: "pointer",
          display: "flex",
          height: { xs: 96, md: 104 },
          justifyContent: "center",
          position: "relative",
          width: { xs: 96, md: 104 },
          "@media (max-height: 900px) and (min-width: 900px)": {
            height: 92,
            width: 92,
          },
        })}
      >
        <Box
          component="input"
          type="file"
          accept="image/png"
          onChange={onChange}
          sx={{ display: "none" }}
        />
        {imageLoading ? (
          <CircularProgress size={24} />
        ) : profileImage ? null : (
          <Typography sx={{ fontSize: { xs: 38, md: 44 }, fontWeight: 800 }}>
            {accountInitial}
          </Typography>
        )}

        <Box
          sx={(theme) => ({
            alignItems: "center",
            bgcolor: theme.palette.primary.main,
            borderRadius: theme.shape.radii.pill,
            bottom: 0,
            color: theme.palette.primary.contrastText,
            display: "flex",
            height: 32,
            justifyContent: "center",
            position: "absolute",
            right: 0,
            width: 32,
          })}
        >
          <PhotoCameraRounded sx={{ fontSize: 17 }} />
        </Box>
      </Box>

      <Typography
        sx={(theme) => ({
          color: theme.palette.text.primary,
          fontSize: theme.typography.caption.fontSize,
          fontWeight: 700,
          lineHeight: "18px",
          mt: 1.25,
          textAlign: "center",
        })}
      >
        Foto da empresa
      </Typography>
      <Typography
        sx={(theme) => ({
          color: helperText ? theme.palette.error.main : theme.palette.text.disabled,
          fontSize: theme.typography.caption.fontSize,
          lineHeight: "18px",
          textAlign: "center",
        })}
      >
        {helperText || "PNG ou JPG"}
      </Typography>
    </Box>
  );
}

function CompleteSignupSection({ children, title }) {
  return (
    <Box
      sx={{
        mt: 0,
        "& + &": { mt: { xs: 3, md: 3 } },
        "@media (max-height: 900px) and (min-width: 900px)": {
          "& + &": { mt: 2 },
        },
      }}
    >
      <Typography
        component="h2"
        sx={(theme) => ({
          alignItems: "center",
          color: theme.palette.text.primary,
          display: "flex",
          fontSize: 16,
          fontWeight: 700,
          gap: 1,
          lineHeight: 1.35,
          mb: 1.5,
          "@media (max-height: 900px) and (min-width: 900px)": {
            mb: 1,
          },
          "&::before": {
            bgcolor: theme.palette.primary.main,
            borderRadius: theme.shape.radii.pill,
            content: '""',
            display: "inline-block",
            height: 18,
            width: 4,
          },
        })}
      >
        {title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: { xs: 2, md: 2 },
          "@media (max-height: 900px) and (min-width: 900px)": {
            gap: 1.5,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function CompleteSignupField({
  children,
  helperText,
  label,
  required = true,
  sx,
  ...props
}) {
  const inputId = `complete-signup-${props.name}`;

  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Typography
        component="label"
        htmlFor={inputId}
        sx={(theme) => ({
          color: theme.palette.text.primary,
          display: "block",
          fontSize: theme.typography.body2.fontSize,
          fontWeight: 500,
          lineHeight: "20px",
          mb: 0.75,
        })}
      >
        {label}
        {required ? " *" : ""}
      </Typography>
      <TextField
        {...props}
        id={inputId}
        fullWidth
        helperText={helperText}
        required={required}
      >
        {children}
      </TextField>
    </Box>
  );
}
